'use client'

import type { ReactNode } from 'react'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Activity, Trophy } from 'lucide-react'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { ActivityView } from './activity-tab'
import type { RenderToLessonModuleLink } from './activity-tab/lesson-history'
import { useSharedTRPC } from '../../context'
import { ActivityTab } from './activity-tab'
import { Header } from './header'
import { MyAchievement } from './my-achievement'

export { BadgeCard, AchievementEmpty, AchievementGridSkeleton, AchievementLoadError } from './my-achievement'
export type { BadgeCardProps, BadgeCardBadge } from './my-achievement'

const tabTriggerClassName = cn(
  'srd:group/tab-profile srd:relative srd:flex srd:h-16! srd:flex-1 srd:flex-col srd:items-stretch srd:min-h-0',
  'srd:gap-0! srd:p-0! srd:justify-between!',
  'srd:rounded-none srd:border-0 srd:border-transparent srd:bg-transparent',
  'srd:text-grey-70! srd:transition-colors srd:hover:text-grey-60 srd:data-active:text-navy-50!',
  'srd:after:hidden!',
)

const tabBodyClassName =
  'srd:flex srd:min-h-0 srd:flex-1 srd:flex-col srd:items-center srd:justify-center srd:gap-1 srd:px-1 srd:sm:gap-2 srd:sm:px-2'

const tabIndicatorClassName = cn(
  'srd:pointer-events-none srd:relative srd:z-1 srd:-mb-px srd:h-[3px] srd:w-full srd:max-w-[100px] srd:shrink-0 srd:self-center srd:rounded-t-full',
  'srd:bg-transparent srd:transition-colors srd:duration-200',
  'srd:group-data-active/tab-profile:bg-blue-70',
)

const tabLabelClassName = cn(
  typographyVariants({ variant: 'title-sm' }),
  'srd:hidden srd:text-center srd:text-current srd:transition-colors srd:sm:block',
)

export type ChildProfileTab = 'aktivitas' | 'prestasi'

export type ChildProfileOptions =
  | {
      type: 'school'
    }
  | {
      type: 'web'
      typeOfUser: 'main'
    }
  | {
      type: 'web'
      typeOfUser: 'child'
    }

export interface ChildProfileProps {
  childId: string
  options: ChildProfileOptions
  tab?: ChildProfileTab
  onTabChange?: (tab: ChildProfileTab) => void
  activityView?: ActivityView
  onActivityViewChange?: (view: ActivityView) => void
  /** Render prop for the back link. The component supplies the styled back content; the consumer wraps it in its router-specific `<Link>`. Omit to hide the back link. */
  renderBackLink?: (content: ReactNode) => ReactNode
  /** Render prop for the (app-specific) edit modal. The component owns the open state and edit trigger; the consumer supplies the modal wired to `user`. */
  renderEditModal?: (args: { open: boolean; onOpenChange: (open: boolean) => void }) => ReactNode
  /** Render prop for the lesson-history "Lihat" CTA. The consumer wraps the supplied content in its router `<Link>` to `/belajar/t/misi`. Omit to hide the CTA. */
  renderToLessonModuleLink?: RenderToLessonModuleLink
}

export function ChildProfile({
  childId,
  options,
  tab,
  onTabChange,
  activityView,
  onActivityViewChange,
  renderBackLink,
  renderEditModal,
  renderToLessonModuleLink,
}: ChildProfileProps) {
  const trpc = useSharedTRPC()
  // Only the child themselves can claim badges. Parents (web `main`) and school viewers get a
  // read-only view: no unclaimed notification dots, and tapping a badge opens the details modal directly.
  const canClaim = options.type === 'web' && options.typeOfUser === 'child'
  const { data: unclaimed } = useQuery(
    trpc.achievement.hasUnclaimed.queryOptions({ userId: childId }, { enabled: canClaim }),
  )
  const hasUnclaimed = canClaim && (unclaimed?.hasUnclaimed ?? false)

  const [internalTab, setInternalTab] = useState<ChildProfileTab>('aktivitas')
  const currentTab = tab ?? internalTab
  const setTab = (next: ChildProfileTab) => {
    if (tab === undefined) setInternalTab(next)
    onTabChange?.(next)
  }

  const [editOpen, setEditOpen] = useState(false)

  return (
    <div className='srd:flex srd:flex-col srd:pb-20 srd:w-full'>
      <Header
        childId={childId}
        options={options}
        onEditClick={renderEditModal ? () => setEditOpen(true) : undefined}
        renderBackLink={renderBackLink}
        className='srd:pt-10'
      />
      <section className='srd:bg-neutrals-white srd:w-full srd:flex srd:flex-col srd:gap-y-10'>
        <Tabs value={currentTab} onValueChange={(value) => setTab(value as ChildProfileTab)}>
          <TabsList
            variant='line'
            className={cn(
              'srd:w-full srd:rounded-none srd:border-b srd:border-grey-30 srd:bg-transparent',
              'srd:h-auto! srd:min-h-0! srd:p-0! srd:items-stretch srd:gap-0',
            )}
          >
            <TabsTrigger value='aktivitas' className={tabTriggerClassName}>
              <span className={tabBodyClassName}>
                <span className='srd:inline-flex srd:shrink-0 srd:text-current'>
                  <Activity className='srd:size-6' />
                </span>
                <span className={tabLabelClassName}>Aktivitas</span>
              </span>
              <span aria-hidden className={tabIndicatorClassName} />
            </TabsTrigger>
            <TabsTrigger value='prestasi' className={tabTriggerClassName}>
              <span className={tabBodyClassName}>
                <span className='srd:relative srd:inline-flex srd:shrink-0 srd:text-current'>
                  <Trophy className='srd:size-6' />
                  {hasUnclaimed && (
                    <span aria-hidden className='srd:absolute srd:-right-1 srd:-top-1 srd:flex srd:size-3'>
                      <span className='srd:absolute srd:inline-flex srd:size-full srd:animate-ping srd:rounded-full srd:bg-error srd:opacity-75' />
                      <span className='srd:relative srd:inline-flex srd:size-3 srd:rounded-full srd:border-2 srd:border-neutrals-white srd:bg-error' />
                    </span>
                  )}
                </span>
                <span className={tabLabelClassName}>Prestasi</span>
              </span>
              <span aria-hidden className={tabIndicatorClassName} />
            </TabsTrigger>
          </TabsList>
          <TabsContent value='aktivitas' className='srd:w-full srd:px-2'>
            <ActivityTab
              childId={childId}
              options={options}
              view={activityView}
              onViewChange={onActivityViewChange}
              renderToLessonModuleLink={renderToLessonModuleLink}
              className='srd:mt-4'
            />
          </TabsContent>
          <TabsContent value='prestasi' className='srd:w-full srd:px-2'>
            <MyAchievement userId={childId} canClaim={canClaim} className='srd:mt-4' />
          </TabsContent>
        </Tabs>
      </section>

      {renderEditModal?.({ open: editOpen, onOpenChange: setEditOpen })}
    </div>
  )
}
